import { computed } from 'vue';
import { useMapGetter } from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';
import { getUserPermissions } from 'dashboard/helper/permissionsHelper';
import { CONTACT_PHONE_HIDE } from 'dashboard/constants/permissions';

/**
 * Composable for masking contact phone numbers based on user permissions.
 * When user has 'contact_phone_hide' permission, phone is masked: +5562930****7
 * (4 digits masked, last digit visible)
 */
export function usePhoneMask() {
  const user = useMapGetter('getCurrentUser');
  const { accountId } = useAccount();

  /**
   * Get custom role permissions for current user using the same pattern as usePolicy
   */
  const userPermissions = computed(() => {
    return getUserPermissions(user.value, accountId.value);
  });

  /**
   * Get current user role from accounts array
   */
  const currentUserRole = computed(() => {
    if (!user.value?.accounts) return null;
    const currentAccount = user.value.accounts.find(
      account => Number(account.id) === Number(accountId.value)
    );
    return currentAccount?.role || 'agent';
  });

  /**
   * Check if current user should see masked phone numbers
   * Administrators always see full phone, regardless of permissions
   */
  const shouldMaskPhone = computed(() => {
    // Administrators always see full phone
    if (currentUserRole.value === 'administrator') return false;

    return userPermissions.value.includes(CONTACT_PHONE_HIDE);
  });

  /**
   * Check if current user can edit contacts (full access to all fields)
   * Users with contact_phone_hide permission cannot edit contacts
   */
  const canEditContact = computed(() => {
    // Administrators can always edit
    if (currentUserRole.value === 'administrator') return true;

    // If user has contact_phone_hide, they cannot edit sensitive fields
    return !userPermissions.value.includes(CONTACT_PHONE_HIDE);
  });

  /**
   * Check if current user can edit basic contact info (name, email only)
   * Users with contact_phone_hide CAN still edit basic info
   * This allows partial editing while protecting phone and other sensitive data
   */
  const canEditBasicInfo = computed(() => {
    // Everyone can edit basic info (name, email)
    // This includes users with contact_phone_hide permission
    return true;
  });

  /**
   * Mask phone number: +5562930****7 (4 digits masked, last visible)
   * @param {string} phone - Phone number to mask
   * @returns {string} - Masked phone or original if not applicable
   */
  const maskPhone = phone => {
    // Normalize falsy values to empty string for consistent template rendering
    if (!phone) return '';
    if (!shouldMaskPhone.value) return phone;

    // Convert to string to handle numeric inputs
    const phoneStr = String(phone);

    // For very short numbers (5 chars or less), mask all except last digit
    if (phoneStr.length <= 5) {
      return '****' + phoneStr.slice(-1);
    }

    // Mask 4 digits before the last one
    // Example: +5562993041234 -> +55629930****4
    const lastDigit = phoneStr.slice(-1);
    const beforeMask = phoneStr.slice(0, -5);
    return `${beforeMask}****${lastDigit}`;
  };

  /**
   * Mask identifier (JID/LID from WhatsApp) - completely hidden when permission active
   * Returns null to hide the entire element via v-if
   * @param {string} identifier - Identifier to mask (e.g., 556299999999@s.whatsapp.net)
   * @returns {string|null} - Original identifier or null if should be hidden
   */
  const maskIdentifier = identifier => {
    if (!identifier) return null;
    if (!shouldMaskPhone.value) return identifier;
    // Completely hide identifier when permission is active
    return null;
  };

  return {
    shouldMaskPhone,
    canEditContact,
    canEditBasicInfo,
    maskPhone,
    maskIdentifier,
  };
}
